import React, { useEffect, useRef, useState } from "react";
import { X, Forward, Share2, Download, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { saveMediaToGallery, shareMediaFile } from "@/lib/saveMedia";
import { toastSaveResult } from "@/components/LightboxMenu";
import { useBackHandler } from "@/lib/backButton";
import { REACT_EMOJIS } from "./emoji";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { ZoomableImage } from "@/components/ZoomableImage";

// Full-screen media viewer (photo/video album) with swipe nav, playback speed,
// download/share, forward and quick reactions. Self-contained: owns index + speed.
export const ChatLightbox = ({ lightbox, t, onClose, onForward, onReact }) => {
  const [index, setIndex] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [busy, setBusy] = useState(null);
  const videoRef = useRef(null);
  const swipe = useRef(null);
  useEffect(() => { setIndex(lightbox?.index || 0); setSpeed(1); }, [lightbox]);
  // The viewer must consume the Android back button FIRST → "back" closes the
  // image/video and returns to the conversation, not to the inbox.
  useBackHandler(!!lightbox, () => { onClose(); return true; });
  if (!lightbox) return null;

  const items = lightbox.items && lightbox.items.length ? lightbox.items : [{ url: lightbox.url, type: "image" }];
  const idx = Math.max(0, Math.min(index, items.length - 1));
  const cur = items[idx] || {};
  const go = (delta) => setIndex((i) => Math.max(0, Math.min(items.length - 1, i + delta)));
  const cycleSpeed = () => {
    const order = [1, 1.5, 2];
    const next = order[(order.indexOf(speed) + 1) % order.length] ?? 1;
    setSpeed(next);
    if (videoRef.current) videoRef.current.playbackRate = next;
  };
  // ⤓ : galerie du téléphone dans l'app (plugin natif, lot 578) ; web → téléchargement. Partager : la photo / vidéo elle-même.
  const kind = cur.type === "video" ? "video" : "image";
  const download = async () => {
    if (busy) return;
    setBusy("download");
    try { toastSaveResult(t, await saveMediaToGallery({ url: cur.url, kind })); }
    catch { toastSaveResult(t, "error"); }
    finally { setBusy(null); }
  };
  const share = async () => {
    if (busy) return;
    setBusy("share");
    try { await shareMediaFile({ url: cur.url, kind, title: "Yalola" }); }
    catch { toast.error(t("viewer.share_failed", "Partage impossible. Réessaie.")); }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex flex-col bg-black/95 animate-in fade-in" data-testid="chat-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-2 shrink-0 text-white" style={{ paddingTop: "calc(0.75rem + var(--sa-top))" }}>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10" data-testid="chat-lightbox-close"><X size={24} /></button>
        {items.length > 1 && <div className="text-sm font-semibold tabular-nums" data-testid="chat-lightbox-counter">{idx + 1} / {items.length}</div>}
        <div className="flex items-center gap-1">
          {cur.type === "video" && (
            <button onClick={(e) => { e.stopPropagation(); cycleSpeed(); }} className="h-10 min-w-[40px] px-2.5 grid place-items-center rounded-full hover:bg-white/10 text-sm font-bold tabular-nums" data-testid="chat-lightbox-speed">{speed}×</button>
          )}
          {lightbox.messageId && (
            <button onClick={(e) => { e.stopPropagation(); onForward(lightbox.messageId); }} className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10" data-testid="chat-lightbox-forward"><Forward size={22} /></button>
          )}
          <button onClick={(e) => { e.stopPropagation(); share(); }} disabled={!!busy} className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10 disabled:opacity-60" aria-label={kind === "video" ? t("viewer.share_video", "Partager la vidéo") : t("viewer.share_image", "Partager l'image")} data-testid="chat-lightbox-share">{busy === "share" ? <Loader2 size={22} className="animate-spin" /> : <Share2 size={22} />}</button>
          <button onClick={(e) => { e.stopPropagation(); download(); }} disabled={!!busy} className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10 disabled:opacity-60" aria-label={t("chat.save_to_gallery", "Enregistrer dans la galerie")} data-testid="chat-lightbox-download" data-kind={kind}>{busy === "download" ? <Loader2 size={22} className="animate-spin" /> : <Download size={22} />}</button>
        </div>
      </div>
      <div
        className="relative flex-1 flex items-center justify-center p-2 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => { swipe.current = e.clientX; }}
        onPointerUp={(e) => { const dx = e.clientX - (swipe.current || e.clientX); if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); swipe.current = null; }}
      >
        {items.length > 1 && idx > 0 && (
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-2 z-10 h-10 w-10 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/60" data-testid="chat-lightbox-prev"><ChevronLeft size={26} /></button>
        )}
        {cur.type === "video"
          ? <video key={cur.url} ref={videoRef} onLoadedMetadata={(e) => { e.currentTarget.playbackRate = speed; }} src={cur.url} poster={cur.poster || NEUTRAL_POSTER} controls autoPlay playsInline className="max-h-full max-w-full object-contain rounded-lg" data-testid="chat-lightbox-video" />
          : <ZoomableImage key={cur.url} src={cur.url} testId="chat-lightbox-image" onSwipe={items.length > 1 ? go : undefined} />}
        {items.length > 1 && idx < items.length - 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-2 z-10 h-10 w-10 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/60" data-testid="chat-lightbox-next"><ChevronRight size={26} /></button>
        )}
      </div>
      {lightbox.messageId && (
        <div className="flex items-center justify-center gap-3 shrink-0" style={{ paddingBottom: "calc(1.25rem + var(--sa-bottom))", paddingTop: "0.75rem" }} onClick={(e) => e.stopPropagation()} data-testid="chat-lightbox-reactions">
          {REACT_EMOJIS.map((e) => (
            <button key={e} onClick={() => { onReact(lightbox.messageId, e); onClose(); }} className="text-3xl hover:scale-125 transition-transform" data-testid={`chat-lightbox-react-${e}`}>{e}</button>
          ))}
        </div>
      )}
    </div>
  );
};
